"use client";

// Показ тостов портала. Один экземпляр живёт в оболочке (portal-frame),
// сообщения приходят через шину из lib/toast.
//
// ── Стопка (№28 каталога движения) ───────────────────────────────────────────
// Три сообщения подряд — обычное дело: синхронизация присылает серию. Столбиком
// они занимали пол-экрана и закрывали содержимое. Теперь плашки лежат СТОПКОЙ:
// видно верхнюю и края тех, что под ней; навёл курсор — разъезжаются. Пока
// курсор над стопкой, обратный отсчёт остановлен, иначе «Вернуть» исчезает
// ровно в тот момент, когда до него тянутся мышкой.

import { useEffect, useState } from "react";
import { IconClose } from "@/components/ui/icons";
import { dismiss, pauseToasts, resumeToasts, subscribeToasts, type ToastItem } from "@/lib/toast";
import { Z } from "@/lib/z";
import { Button } from "@/components/ui/button";

const TONE: Record<ToastItem["kind"], { color: string; label: string }> = {
  error: { color: "var(--danger)", label: "Ошибка" },
  success: { color: "var(--success)", label: "Готово" },
  info: { color: "var(--primary)", label: "" },
  undo: { color: "var(--primary)", label: "" },
};

/** Сколько плашек показываем в свёрнутой стопке. Остальные ждут очереди. */
const STACK_VISIBLE = 3;

export function ToastHost() {
  const [items, setItems] = useState<ToastItem[]>([]);
  const [open, setOpen] = useState(false);
  useEffect(() => subscribeToasts(setItems), []);

  if (!items.length) return null;

  // Свежая плашка — последняя в списке и лежит СПЕРЕДИ стопки (ближе к низу
  // экрана). Индекс от конца задаёт глубину: 0 — верхняя, дальше уходят назад.
  const shown = open ? items : items.slice(-STACK_VISIBLE);

  return (
    <div
      aria-live="polite"
      // Тост — самый верхний слой (lib/z.ts). Было z-[100]: сообщение об ошибке
      // пряталось под любой модалкой выше сотни, то есть почти под всеми.
      style={{ zIndex: Z.TOAST }}
      className="toast-stack pointer-events-none fixed bottom-4 left-1/2 flex w-[min(560px,calc(100vw-32px))] -translate-x-1/2 flex-col"
      onMouseEnter={() => { setOpen(true); pauseToasts(); }}
      onMouseLeave={() => { setOpen(false); resumeToasts(); }}
      onFocus={() => { setOpen(true); pauseToasts(); }}
      onBlur={() => { setOpen(false); resumeToasts(); }}
    >
      {shown.map((t, i) => {
        const tone = TONE[t.kind];
        const depth = shown.length - 1 - i; // 0 — передняя плашка
        return (
          <div
            key={t.id}
            className="toast-item pointer-events-auto"
            style={{
              zIndex: i,
              // Свёрнутая стопка: задние уезжают назад и вглубь, поэтому видны
              // только их края. Развёрнутая — обычный столбик с зазором.
              marginBottom: open ? 8 : depth === 0 ? 0 : -46,
              transform: open ? undefined : `scale(${1 - depth * 0.04}) translateY(${depth * 6}px)`,
              opacity: open ? 1 : depth > 1 ? 0.55 : 1,
            }}
          >
            <div
              className="relative flex items-start gap-3 overflow-hidden rounded-[var(--radius-lg)] px-4 py-3"
              style={{
                color: "var(--text)",
                border: `1px solid color-mix(in srgb, ${tone.color} 40%, var(--border))`,
                background: `color-mix(in srgb, ${tone.color} 9%, var(--surface))`,
                boxShadow: "0 14px 40px rgba(0,0,0,.18)",
              }}
            >
              <span
                aria-hidden
                className="mt-[6px] h-2 w-2 shrink-0 rounded-full"
                style={{ background: tone.color }}
              />
              <div className="min-w-0 flex-1">
                {tone.label && (
                  <div className="text-[11px] font-bold" style={{ color: tone.color }}>
                    {tone.label}
                  </div>
                )}
                <div className="text-[12px] leading-snug">{t.text}</div>
              </div>
              {t.retry && (
                <button
                  type="button"
                  onClick={() => { dismiss(t.id); t.retry?.(); }}
                  className="glass-hover shrink-0 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1 text-[12px] font-semibold"
                >
                  Повторить
                </button>
              )}
              {t.undo && (
                <button
                  type="button"
                  onClick={() => { dismiss(t.id); t.undo?.(); }}
                  className="glass-hover shrink-0 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1 text-[12px] font-semibold"
                >
                  Вернуть
                </button>
              )}
              <Button iconOnly onClick={() => dismiss(t.id)} aria-label="Закрыть">
                <IconClose size={13} />
              </Button>

              {/* Полоса обратного отсчёта — только у отмены: здесь важно ВИДЕТЬ,
                  сколько осталось, чтобы успеть вернуть. У прочих плашек она
                  была бы лишним движением на экране. */}
              {t.undo && (
                <span
                  aria-hidden
                  className="toast-countdown"
                  style={{ background: tone.color, animationDuration: `${t.ttl}ms` }}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
